import { useCallback, useEffect, useRef, useState } from 'react';
import { getMeetingPopupParentOrigin } from '../services/meetingWindow/popupMode';
import {
  isMeetingWindowMessage,
  MEETING_WINDOW_MESSAGE_SOURCE,
  type MeetingWindowAuthSnapshot,
} from '../services/meetingWindow/protocol';
import type { UserInfo } from '../types/user';
import { decodeJWT } from '../utils/decodeJWT';
import type { HostAuthState } from './useHostAuth';

const decodeIdToken = (token: string | null): UserInfo | null => {
  if (!token) {
    return null;
  }
  try {
    return decodeJWT(token) as UserInfo;
  } catch (err) {
    console.warn('[usePopupHostAuth] failed to decode id token', err);
    return null;
  }
};

/**
 * If the opener never answers the initial `ready`, surface an error rather than
 * spinning forever. Shared with the settings leg of the same handshake so both
 * halves give up together rather than on two independently-chosen deadlines.
 */
export const HANDSHAKE_TIMEOUT_MS = 10000;
// Per-request fallback so token getters don't hang if the opener goes away.
const REQUEST_AUTH_TIMEOUT_MS = 4000;

/**
 * Auth hook for the meeting popup window (hosted-mode launch). Mirrors
 * `HostAuthState` but sources the identity from the opener over `postMessage`
 * instead of `window.PKCEAuth`. The popup runs standalone NG Meet and has no
 * host bridge of its own, so the opener (the embedded remote) hands it the
 * host tokens. See `services/meetingWindow/protocol.ts`.
 */
export const usePopupHostAuth = (): HostAuthState => {
  const [snapshot, setSnapshot] = useState<MeetingWindowAuthSnapshot>({
    idToken: null,
    accessToken: null,
  });
  const [idTokenData, setIdTokenData] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const parentOriginRef = useRef<string | null>(getMeetingPopupParentOrigin());
  const snapshotRef = useRef<MeetingWindowAuthSnapshot>(snapshot);
  snapshotRef.current = snapshot;
  const pendingRef = useRef(new Map<string, (snap: MeetingWindowAuthSnapshot) => void>());

  const applySnapshot = useCallback((next: MeetingWindowAuthSnapshot) => {
    setSnapshot(next);
    setIdTokenData(decodeIdToken(next.idToken));
    setIsLoading(false);
    setError(next.idToken ? null : 'No host session was provided to the meeting window.');
  }, []);

  useEffect(() => {
    const opener = window.opener as Window | null;
    const parentOrigin = parentOriginRef.current;
    if (!(opener && parentOrigin)) {
      setIsLoading(false);
      setError('Meeting window was opened without a host session.');
      return;
    }

    const pending = pendingRef.current;

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== parentOrigin || event.source !== opener) {
        return;
      }
      if (!isMeetingWindowMessage(event.data) || event.data.type !== 'auth') {
        return;
      }
      const { auth, requestId } = event.data;
      applySnapshot(auth);
      if (requestId) {
        const resolver = pending.get(requestId);
        if (resolver) {
          pending.delete(requestId);
          resolver(auth);
        }
      }
    };

    window.addEventListener('message', onMessage);
    opener.postMessage({ source: MEETING_WINDOW_MESSAGE_SOURCE, type: 'ready' }, parentOrigin);

    const timeoutId = window.setTimeout(() => {
      setIsLoading((wasLoading) => {
        if (wasLoading && !snapshotRef.current.idToken) {
          setError('Timed out waiting for the host session.');
          return false;
        }
        return wasLoading;
      });
    }, HANDSHAKE_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeoutId);
    };
  }, [applySnapshot]);

  // Ask the opener for a fresh identity and resolve with it (falling back to
  // the cached snapshot on timeout). Used by the per-request token getters so
  // a host-side token rotation doesn't strand the meeting with a stale bearer.
  const requestAuth = useCallback((): Promise<MeetingWindowAuthSnapshot> => {
    const opener = window.opener as Window | null;
    const parentOrigin = parentOriginRef.current;
    if (!(opener && parentOrigin)) {
      return Promise.resolve(snapshotRef.current);
    }
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        pendingRef.current.delete(requestId);
        resolve(snapshotRef.current);
      }, REQUEST_AUTH_TIMEOUT_MS);
      pendingRef.current.set(requestId, (snap) => {
        window.clearTimeout(timeoutId);
        resolve(snap);
      });
      opener.postMessage(
        { source: MEETING_WINDOW_MESSAGE_SOURCE, type: 'request-auth', requestId },
        parentOrigin,
      );
    });
  }, []);

  const getIdToken = useCallback(async () => (await requestAuth()).idToken, [requestAuth]);
  const getAccessToken = useCallback(async () => (await requestAuth()).accessToken, [requestAuth]);

  const login = useCallback(() => {
    // No login flow in the popup — identity comes from the opener.
  }, []);
  const logout = useCallback(() => {
    window.close();
  }, []);

  return {
    accessToken: snapshot.accessToken,
    idToken: snapshot.idToken,
    idTokenData,
    isAuthenticated: !!snapshot.idToken && !!idTokenData,
    isLoading,
    error,
    login,
    logout,
    getAccessToken,
    getIdToken,
  };
};
